// Selection-aware shortcuts for duplicate + z-order + group/lock. Sits in
// BoardPage so it can see both the doc and the selection state. Keys:
//   Cmd/Ctrl + D          duplicate (selection becomes the new copies)
//   Cmd/Ctrl + ]          bring to front
//   Cmd/Ctrl + [          send to back
//   Cmd/Ctrl + G          group selection
//   Cmd/Ctrl + Shift + G  ungroup selection
//   Cmd/Ctrl + Shift + L  toggle lock on selection
// The text-format toolbar owns Cmd+B/I/U; the undo manager owns Cmd+Z/Y.

import { useEffect } from 'react';
import * as Y from 'yjs';
import {
  bringToFront,
  duplicateShapes,
  groupShapes,
  sendToBack,
  toggleLocked,
  ungroupShapes,
} from './shapeOps';

interface Args {
  doc: Y.Doc | null;
  selectedIds: Set<string>;
  setSelectedIds: (ids: Set<string>) => void;
  authorId: string;
}

export function useShapeShortcuts({ doc, selectedIds, setSelectedIds, authorId }: Args) {
  useEffect(() => {
    if (!doc) return;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      const target = e.target as HTMLElement | null;
      if (target && isFormField(target)) return;
      if (selectedIds.size === 0) return;
      const k = e.key.toLowerCase();

      if (k === 'd' && !e.shiftKey) {
        e.preventDefault();
        const newIds = duplicateShapes(doc, selectedIds, authorId);
        if (newIds.length > 0) setSelectedIds(new Set(newIds));
      } else if (e.key === ']') {
        e.preventDefault();
        bringToFront(doc, selectedIds);
      } else if (e.key === '[') {
        e.preventDefault();
        sendToBack(doc, selectedIds);
      } else if (k === 'g' && !e.shiftKey) {
        e.preventDefault();
        groupShapes(doc, selectedIds);
      } else if (k === 'g' && e.shiftKey) {
        e.preventDefault();
        ungroupShapes(doc, selectedIds);
      } else if (k === 'l' && e.shiftKey) {
        e.preventDefault();
        toggleLocked(doc, selectedIds);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [doc, selectedIds, setSelectedIds, authorId]);
}

function isFormField(el: HTMLElement): boolean {
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'INPUT') return true;
  return el.isContentEditable;
}
